import React, { useEffect, useState, useRef, useMemo } from "react";
import PolotnoPreview from "./PolotnoPreview";

/**
 * TemplatePreviewRenderer - Renders Polotno templates using hidden Workspace components
 * Each template gets a hidden PolotnoPreview that mounts a <Workspace />, renders, and exports as PNG
 * Uses progressive rendering to show templates one at a time for faster perceived loading
 */
const TemplatePreviewRenderer = ({ templates = [], onPreviewGenerated }) => {
  // Delay rendering to allow dynamic content injection to complete
  const [shouldRender, setShouldRender] = useState(false);
  // Track which templates should be rendered (progressive loading)
  const [renderingIndex, setRenderingIndex] = useState(0);
  // Track which template IDs have already been rendered
  const [renderedTemplates, setRenderedTemplates] = useState(new Set());
  // Track template content hashes to detect actual changes
  const templateHashesRef = useRef(new Map());

  // Calculate content hash for a template to detect changes
  const getTemplateContentHash = (template) => {
    if (!template?.template?.pages?.[0]?.children) return "";
    // Create a simple hash of the text content to detect changes
    const textContent = template.template.pages[0].children
      .filter((child) => child.type === "text")
      .map((child) => child.text || "")
      .join("|");
    return `${template.id}_${textContent}`;
  };

  // Memoize template IDs to prevent array recreation issues
  const templateIds = useMemo(() => {
    return templates.map((t) => t.id).join(",");
  }, [templates]);

  useEffect(() => {
    // Only start rendering if we have templates
    if (!templates || templates.length === 0) {
      return;
    }

    // Check if there are NEW templates or CHANGED content
    const changedTemplates = templates.filter((t) => {
      const currentHash = getTemplateContentHash(t);
      const previousHash = templateHashesRef.current.get(t.id);

      // Template is new or content has changed
      if (!previousHash || previousHash !== currentHash) {
        templateHashesRef.current.set(t.id, currentHash);
        return true;
      }
      return false;
    });

    if (
      changedTemplates.length === 0 &&
      renderedTemplates.size === templates.length
    ) {
      console.log(
        "✅ All templates already rendered with same content, skipping"
      );
      return;
    }

    if (changedTemplates.length > 0) {
      console.log(
        `⏳ Template content changed for ${changedTemplates.length} template(s), waiting for content injection...`
      );
      // Reset rendered set for changed templates only
      setRenderedTemplates((prev) => {
        const newSet = new Set(prev);
        changedTemplates.forEach((t) => newSet.delete(t.id));
        return newSet;
      });
    }

    // Check if templates have been enhanced with dynamic content
    const hasEnhancedContent = templates.some((t) =>
      t.template?.pages?.[0]?.children?.some(
        (child) =>
          child.text !== "Your Headline Here" &&
          child.text !== "Merry Christmas" &&
          child.text !== "Your compelling subheading here"
      )
    );

    if (hasEnhancedContent) {
      console.log("✅ Templates already have dynamic content");
    }

    // Wait for dynamic content injection to complete
    const timer = setTimeout(
      () => {
        console.log("✅ Starting preview generation for new/changed templates");
        setShouldRender(true);
        // Start rendering from the first unrendered template
        const firstUnrenderedIndex = templates.findIndex(
          (t) => !renderedTemplates.has(t.id)
        );
        if (firstUnrenderedIndex >= 0) {
          setRenderingIndex(Math.max(renderingIndex, firstUnrenderedIndex));
        }
      },
      hasEnhancedContent ? 300 : 800
    ); // Shorter wait times

    return () => clearTimeout(timer);
  }, [templateIds, templates, renderingIndex, renderedTemplates]); // More specific dependencies

  const handlePreviewReady = (templateId, url) => {
    console.log(`✅ Preview ready for ${templateId}`);

    // Mark this template as rendered
    setRenderedTemplates((prev) => new Set([...prev, templateId]));

    if (onPreviewGenerated) {
      onPreviewGenerated(templateId, url);
    }

    // After a template finishes, start rendering the next one
    setRenderingIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      if (nextIndex < templates.length) {
        console.log(
          `🚀 Starting render for template ${nextIndex + 1}/${templates.length}`
        );
      }
      return nextIndex;
    });
  };

  if (!shouldRender || !templates.length) {
    return null;
  }

  // Render all templates up to the current rendering index
  // Keep them all mounted so blob URLs stay valid
  const templatesToRender = templates.slice(0, renderingIndex + 1);

  if (templatesToRender.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "none" }}>
      {templatesToRender.map((template) => {
        // CRITICAL: Keep all components mounted to preserve blob URLs
        // If already rendered, keep the component but mark it as complete
        const isComplete = renderedTemplates.has(template.id);
        // Use content hash as part of the key so React remounts PolotnoPreview
        // when template text content changes (e.g., after dynamic injection)
        const contentHash = getTemplateContentHash(template);

        return (
          <div
            key={contentHash || template.id}
            style={{
              width: "1080px",
              height: "1080px",
              position: "absolute",
              top: "-9999px",
              left: "-9999px"
            }}
            data-status={isComplete ? "complete" : "rendering"}
          >
            <PolotnoPreview
              templateJSON={template.template}
              onPreviewReady={(url) => handlePreviewReady(template.id, url)}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TemplatePreviewRenderer;
